import { loadFeatureConversations, deleteFeatureConversation } from '@/api/query/feature';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ANALYTICS_EVENT } from '@/common/constants/analyticsEvents';
import { useLanguage } from '@/common/hooks/useLanguageData';
import { useAnalytics } from '@/common/contexts/AnalyticsContext';
import { THEME } from '@/common/styles/theme';
import { CONVERSATIONS_CHANGED, notifyConversationsChanged } from '@/common/utils/conversationEvents';

const DELETE_MODAL_CLOSE_DELAY_MS = 900;

const DATE_LOCALE_BY_LANG = {
	pl: 'pl-PL',
	en: 'en-GB',
	de: 'de-DE',
	cs: 'cs-CZ',
};

const normalizeConversations = (payload) => {
	if (Array.isArray(payload)) {
		return payload;
	}

	if (Array.isArray(payload?.data)) {
		return payload.data;
	}

	return [];
};

const shouldAutoOpenFirstConversation = () => {
	if (typeof window === 'undefined') {
		return true;
	}

	return !window.matchMedia(`(max-width: ${THEME.mobileBreakpoint})`).matches;
};

const createClosedDeleteModalState = () => ({
	open: false,
	personId: null,
	phase: 'confirm',
});

const useConversations = () => {
	const { lang } = useLanguage();
	const navigate = useNavigate();
	const { id } = useParams();
	const { trackAnalytics } = useAnalytics();
	const idRef = useRef(id);
	idRef.current = id;

	const closeDeleteModalTimeoutRef = useRef(null);
	const [conversations, setConversations] = useState([]);
	const [hasLoadedConversations, setHasLoadedConversations] = useState(false);
	const [deleteModal, setDeleteModal] = useState(createClosedDeleteModalState);

	const { mutate, isPending } = useMutation({
		mutationFn: (payload) => loadFeatureConversations(payload),
		onSuccess: ({ data }) => {
			const convs = normalizeConversations(data);

			setConversations(convs);
			setHasLoadedConversations(true);

			const sorted = [...convs].sort(
				(a, b) => new Date(b.timestamp) - new Date(a.timestamp),
			);

			if (!idRef.current && sorted.length > 0 && shouldAutoOpenFirstConversation()) {
				navigate(`/chat/${sorted[0].id}`, { replace: true });
			}
		},
	});

	useEffect(() => {
		mutate({ type: 'CHAT', language: lang });
	}, [mutate, lang]);

	useEffect(() => {
		const refresh = (event) => {
			const patch = event?.detail;

			if (patch?.conversationId) {
				const conversationId = String(patch.conversationId);
				const nextTimestamp = patch.timestamp || new Date().toISOString();

				setConversations((prev) =>
					prev.map((conversation) => {
						if (String(conversation.id) !== conversationId) {
							return conversation;
						}

						return {
							...conversation,
							lastMessage:
								patch.lastMessage === undefined
									? conversation.lastMessage
									: patch.lastMessage,
							timestamp: nextTimestamp,
						};
					}),
				);
			}

			mutate({ type: 'CHAT', language: lang });
		};
		window.addEventListener(CONVERSATIONS_CHANGED, refresh);
		return () => window.removeEventListener(CONVERSATIONS_CHANGED, refresh);
	}, [mutate, lang]);

	useEffect(() => () => {
		if (closeDeleteModalTimeoutRef.current) {
			window.clearTimeout(closeDeleteModalTimeoutRef.current);
		}
	}, []);

	const formatTime = (isoString) => {
		if (!isoString) {
			return '';
		}

		const date = new Date(isoString);
		const locale = DATE_LOCALE_BY_LANG[lang] ?? 'pl-PL';

		return date.toLocaleDateString(locale, {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		});
	};

	const deleteConversationMutation = useMutation({
		mutationFn: (personId) => deleteFeatureConversation({
			person: personId,
			type: 'CHAT',
			language: lang,
		}),
		onSuccess: (_data, personId) => {
			setDeleteModal((prev) => ({
				...prev,
				phase: 'deleted',
			}));
			notifyConversationsChanged();

			closeDeleteModalTimeoutRef.current = window.setTimeout(() => {
				setDeleteModal(createClosedDeleteModalState());

				if (String(personId) === String(idRef.current)) {
					navigate('/chat');
				}

				mutate({ type: 'CHAT', language: lang });
			}, DELETE_MODAL_CLOSE_DELAY_MS);
		},
		onError: () => {
			setDeleteModal((prev) => ({
				...prev,
				phase: 'confirm',
			}));
		},
	});

	const handleDeleteRequest = useCallback((personId) => {
		setDeleteModal({
			open: true,
			personId,
			phase: 'confirm',
		});
	}, []);

	const handleCloseDeleteModal = useCallback(() => {
		if (deleteModal.phase !== 'confirm') {
			return;
		}

		setDeleteModal(createClosedDeleteModalState());
	}, [deleteModal.phase]);

	const handleConfirmDelete = useCallback(() => {
		if (!deleteModal.personId || deleteModal.phase !== 'confirm') {
			return;
		}

		trackAnalytics(ANALYTICS_EVENT.CHAT.CONVERSATION_DELETE, {
			conversationId: deleteModal.personId,
		});
		setDeleteModal((prev) => ({
			...prev,
			phase: 'deleting',
		}));
		deleteConversationMutation.mutate(deleteModal.personId);
	}, [deleteConversationMutation, deleteModal.personId, deleteModal.phase]);

	const sortedConversations = useMemo(
		() => [...conversations].sort(
			(a, b) => new Date(b.timestamp) - new Date(a.timestamp),
		),
		[conversations],
	);

	const handleConversationClick = (currentId) => {
		if (deleteModal.open) {
			return;
		}

		if (String(currentId) !== String(id)) {
			trackAnalytics(ANALYTICS_EVENT.CHAT.CONVERSATION_OPEN, {
				conversationId: currentId,
				personId: currentId,
			});
			navigate(`/chat/${currentId}`);
		}
	};

	const isDeleteInProgress = deleteModal.phase === 'deleting' || deleteModal.phase === 'deleted';

	return {
		conversations: sortedConversations,
		isLoading: isPending && !hasLoadedConversations,
		activeConversationId: id,
		handleConversationClick,
		handleDeleteRequest,
		deleteModal,
		handleCloseDeleteModal,
		handleConfirmDelete,
		isDeleteInProgress,
		formatTime,
		reloadConversations: () => mutate({ type: 'CHAT', language: lang }),
	};
};

export default useConversations;
